<template>
  <div>
    <v-card-title>{{ titleText }}</v-card-title>

    <v-card-text>
      <v-form v-model="valid" ref="form">
        <v-container>
          <v-row>

            <!-- ОБЫЧНЫЙ ЭЛЕМЕНТ (НЕ task / question) -->
            <template v-if="isRegular">
              <!-- Название -->
              <label class="pd-lr-10 mb-0">Название элемента</label>
              <v-text-field
                v-model="entityTitle"
                :rules="textRules"
                maxlength="100"
                dense
                required
                class="pt-0 pd-lr-10"
              />

              <!-- Имя (англ.) -->
              <label class="pd-lr-10 mb-0">Имя элемента (на английском)</label>
              <v-text-field
                v-model="entityName"
                :rules="pathRules"
                maxlength="50"
                dense
                required
                class="pt-0 pd-lr-10"
              />

              <!-- Описание -->
              <label class="pd-lr-10 mb-0">Описание</label>
              <v-textarea
                v-model="description"
                required
                class="pt-0"
              />
              
            </template>

            <!-- ЗАДАНИЕ / ВОПРОС -->
            <template v-else>
              <!-- Название -->
              <label class="pd-lr-10">
                {{ isTask ? 'Текст задания' : 'Текст вопроса' }}
              </label>
              <v-textarea
                v-model="entityTitle"
                :rules="textRules"
                outlined
                required
                class="pd-lr-10"
              />

              <!-- Описание -->
              <label class="pd-lr-10">
                {{ isTask ? 'Описание задания' : 'Описание вопроса' }}
              </label>
              <v-textarea
                v-model="description"
                outlined
                required
                class="pd-lr-10"
              />
            </template>

            <!-- ФАЙЛЫ (ТОЛЬКО ДЛЯ ЗАДАНИЯ) -->
            <div v-if="isTask" style="padding: 0;">
              <label class="pd-lr-10" style="width: 100%">Добавьте файлы</label>

              <user-card class="user-card pd-lr-10"" style="padding: 0;">
                <template v-slot:body>
                  <p class="file-error">{{ fileError }}</p>

                  <div class="container-tab rel" @dragover.prevent @drop.prevent>
                    <div 
                      @dragover.prevent="onDragOver"
                      @dragleave.prevent="onDragLeave"
                      @drop.prevent="dragFile"
                      :class="{ 'is-dragover': isDragOver }"
                    >
                      <span
                        v-if="filelist.length === 0"
                        class="drag-text"
                        style="padding: 0; margin-left: 100px; width: 65%; max-height: 25px"
                      >
                        Перетащите файлы для загрузки
                      </span>

                      <table>
                        <tr>
                          <td class="plus-block">
                            <label for="file" style="margin: 0;">
                              <v-icon>ti-plus</v-icon>
                              <input
                                id="file"
                                type="file"
                                multiple
                                accept=".txt,.pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                                @change="addFiles"
                                style="display: none"
                              />
                            </label>
                          </td>

                          <td
                            v-for="file in filelist"
                            :key="file.name"
                            class="newFile custom-i"
                          >
                            <v-btn
                              class="close-button custom-close btn-pd"
                              variant="text"
                              text
                              @click="deleteNewFile(file)"
                            >
                              <v-icon>ti-close</v-icon>
                            </v-btn>

                            <a :href="file.url" target="_blank" :title="file.name">
                              <i class="fa fa-file-o"></i>
                              <span class="text-10" style="padding: 0 5px;">{{ file.name }}</span>
                            </a>
                          </td>
                        </tr>
                      </table>
                    </div>
                  </div>
                </template>
              </user-card>
            </div>

            <!-- ТИП / ЗНАЧИМОСТЬ -->
            <span>Тип: <b>{{ entityType }}</b></span>

            <div class="slider-weight">
              <label>Значимость: <b>{{ sliderValue }}</b></label>
              <input
                v-model="sliderValue"
                type="range"
                min="0.1"
                max="1"
                step="0.1"
                class="slider"
              />
            </div>

            <div class="text-center" style="width: 100%">
              <v-btn
                class="btnSave primary-btn"
                @click.native.prevent="saveNewEntity"
              >
                Сохранить
              </v-btn>
            </div>

          </v-row>
        </v-container>
      </v-form>
    </v-card-text>
  </div>
</template>

<script>
import UserCard from "@/pages/UserProfileTeamplate/UserCard.vue";
import GlobalMethods from "@/plugins/globalMethods";

export default {
  name: "EntityCreationForm",

  components: { UserCard },

  props: {
    entityType: String,
    entityKey: String,
    skillId: Number,
    entityCount: Number,
    taskCount: Number,
    questionCount: Number,
  },

  data() {
    return {
      valid: false,
      entityTitle: "",
      entityName: "",
      description: "",
      sliderValue: 0.1,

      filelist: [],
      fileError: "",

      textRules: [v => !!v || "Поле обязательно"],
      pathRules: [
        v => !!v || "Поле обязательно",
        v => /^[-_A-Za-z0-9]+$/.test(v) || "Недопустимые символы",
      ],

      isDragOver: false,
    };
  },

  computed: {
    isTask() {
      return this.entityType === "task";
    },
    isQuestion() {
      return this.entityType === "question";
    },
    isRegular() {
      return !this.isTask && !this.isQuestion;
    },

    titleText() {
      if (this.isTask) return "Новое задание";
      if (this.isQuestion) return "Новый вопрос";
      return "Создание элемента";
    },
  },

  watch: {
    entityTitle(value) {
      // Для обычных сущностей автоматически подставляем path
      if (this.isRegular) {
        this.entityName = GlobalMethods.textTranslate(value);
      }
    },
  },

  methods: {
    /* ---------- ФАЙЛЫ ---------- */
    addFiles(e) {
      const files = Array.from(e.target.files);
      this.appendFiles(files);
    },

    deleteNewFile(file) {
      this.filelist = this.filelist.filter(f => f.name !== file.name);
    },

    appendFiles(files) {
      this.fileError = "";
      const allowed = ["txt", "pdf", "doc", "docx", "xls", "xlsx", "png", "jpg", "jpeg"];

      files.forEach(file => {
        const ext = file.name.split(".").pop().toLowerCase();

        if (allowed.indexOf(ext) === -1) {
          this.fileError = "Запрещённый формат файла";
          return;
        }

        if (!this.filelist.find(f => f.name === file.name)) {
          file.url = URL.createObjectURL(file);
          this.filelist.push(file);
        }
      });
    },

    /* ---------- СОХРАНЕНИЕ ---------- */
    async saveNewEntity() {
      this.valid = await this.$refs.form.validate();
      if (!this.valid) return;

      let counter = 0;

      if (this.isTask) {
        counter = this.taskCount + 1;
        this.entityName = "task-" + counter;
      } else if (this.isQuestion) {
        counter = this.questionCount + 1;
        this.entityName = "question-" + counter;
      } else {
        counter = this.entityCount + 1;
      }

      const entity = {
        id: counter,
        title: this.entityTitle,
        path: this.entityName,
        description: this.description,
        weight: parseFloat(this.sliderValue),
        type: this.entityType,
        skillId: this.skillId,
        key: this.entityKey,
        mark: "",
      };

      if (this.isTask && this.filelist.length) {
        const formdata = new FormData();
        this.filelist.forEach(file => formdata.append("files[]", file));
        entity.formdata = formdata;
      }

      this.$emit("saveEntity", entity);
    },

    onDragOver() { 
      this.isDragOver = true; 
    },
    onDragLeave() { 
      this.isDragOver = false; 
    },

    dragFile(e) {
      this.isDragOver = false;
      const files = Array.from(e.dataTransfer.files);
      this.appendFiles(files);
    },
  },
};
</script>

<style>
.small-text {
  padding: 0 5px;
  font-size: 12px;
}
.btn-pd span {
  padding: 0 !important;
}
.text-10 {
  font-size: 10px !important;
  max-height: 27px !important;
}
.newFile .v-btn__content {
  padding: 0 !important;
}
.close-button .v-icon {
  font-size: 10px !important;
  display: flex !important;
  align-items: center;
}
</style>
